import Map, { Marker } from 'react-map-gl/maplibre'
import 'maplibre-gl/dist/maplibre-gl.css'
import maplibregl from 'maplibre-gl'
import Styles from './StationDashboardPage.module.scss'
import { useViewModel } from './StationDashboardPage.vm'
import { ChartLineColors, ChartTitle, ChartUnit, MapLayer, MapStyleJson } from 'src/meta'
import { elmIdLookup } from 'src/api'
import { isNil, maxBy } from 'lodash'
import {
  IconDefinition,
  faCloud,
  faDroplet,
  faMapPin,
  faTachometerAlt,
  faTemperatureQuarter,
  faWind,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Chart } from 'src/components/Chart'
import { Spin } from 'antd'
import { LoadingOutlined } from '@ant-design/icons'
import Logo from 'src/assets/logo.png'
import dayjs from 'dayjs'
import { DualChart } from 'src/components/DualChart'
import { WindDirChart } from 'src/components/WindDirChart'
import { useWindowSize } from 'src/hooks/useWindowSize'

export const Icon: Partial<Record<MapLayer, IconDefinition>> = {
  temperature: faTemperatureQuarter,
  humidity: faDroplet,
  cloud: faCloud,
  pressure: faTachometerAlt,
  wind: faWind,
}
const Indexes: MapLayer[] = ['temperature', 'humidity', 'pressure', 'wind', 'wind-direction']
export const StationDashboardPage = () => {
  const { station, obsLookup, latestObsLookup } = useViewModel()
  const { width, height } = useWindowSize()
  const photoPath = process.env.REACT_APP_PHOTO_PAHT

  return (
    <div className={Styles.page}>
      <div className={Styles.header}>
        <div className={Styles.logo}>
          <img src={Logo} />
        </div>
        <div className={Styles.name}>
          <div className={Styles.spacer} />
          <div className={Styles.title}> {'Integrated urban weather monitoring and data-sharing platform for smart cities'} </div>
          <div className={Styles.station}> {station?.name} </div>
          <div className={Styles.spacer} />
        </div>
        <div className={Styles.spacer} />
      </div>
      <div className={Styles.updateTime}>
        <div>{'Updated At'}</div>
        <div>
          {dayjs((maxBy(Object.values(latestObsLookup), 't')?.t ?? (Date.now()))).format('DD/MM/YYYY HH:mm')}
        </div>
      </div>
      <div className={Styles.upper}>
        <div className={Styles.latestObs}>
          {Indexes.map(layer => {
            const elmId = elmIdLookup[layer]
            const readings = latestObsLookup?.[elmId]?.v
            return (
              <div className={Styles.obs}>
                <h2>{ChartTitle[layer]}</h2>
                <FontAwesomeIcon className={Styles.icon} icon={Icon[layer]!} />
                {!isNil(readings) && !isNaN(readings) ? (
                  <div className={Styles.value} style={{ color: ChartLineColors[layer] }}>
                    {readings}
                    <span className={Styles.unit}>{ChartUnit[layer]}</span>
                  </div>
                ) : (
                  <div className={Styles.value}>{'-'}</div>
                )}
              </div>
            )
          })}
        </div>
      </div>
      <div className={Styles.below}>
        <div className={Styles.charts}>
          <div className={Styles.chart}>
            <h2>{`Temperature/Humidity Time Series`}</h2>
            <DualChart
              className={Styles.content}
              data={obsLookup?.[elmIdLookup.temperature] ?? []}
              yAxisUnits={['°C', '%']}
              syncId={'chart'}
              lineColors={[ChartLineColors.temperature!, ChartLineColors.humidity!]}
              width={(width - 20) / 3}
              noMinor
              grid />
          </div>
          <div className={Styles.chart}>
            <h2>{`${ChartTitle.pressure} Time Series`}</h2>
            <Chart
              className={Styles.content}
              data={obsLookup?.[elmIdLookup.pressure] ?? []}
              yAxisUnit={ChartUnit.pressure ?? ''}
              syncId={'chart'}
              lineColor={ChartLineColors.pressure}
              onMove={v => { }}
              noMinor
              grid
            />
          </div>
          <div className={Styles.chart}>
            <h2>{`${ChartTitle.wind} Time Series`}</h2>
            <Chart
              className={Styles.content}
              data={obsLookup?.[elmIdLookup.wind] ?? []}
              yAxisUnit={ChartUnit.wind ?? ''}
              syncId={'chart'}
              lineColor={ChartLineColors.wind}
              minY={0}
              onMove={v => { }}
              noMinor
              grid
            />
          </div>
          <div className={Styles.chart}>
            <h2>{`${ChartTitle['wind-direction']} Time Series`}</h2>
            <WindDirChart
              className={Styles.content}
              data={obsLookup?.[elmIdLookup['wind-direction']] ?? []}
              yAxisUnit={''}
              syncId={'chart'}
              color={ChartLineColors.wind}
              width={(width - 20) / 3}
              noMinor
              grid
            />
          </div>
        </div>
        <div className={Styles.graphics}>
          {photoPath && (
            <div className={Styles.image}>
              <h2>{'Image'}</h2>
              <Spin
                className={Styles.spin}
                size="large"
                indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                spinning
              />
              <img src={photoPath + `/${station?.id}.jpg`} />
            </div>
          )}
          <div className={Styles.map}>
            <h2>{'Location'}</h2>
            <Spin
              className={Styles.spin}
              size="large"
              indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
              spinning
            />
            <Map
              mapLib={maplibregl}
              mapStyle={MapStyleJson.Topo}
              zoom={14}
              latitude={station?.location.latitude ?? 22.3}
              longitude={station?.location.longitude ?? 114.2}
              attributionControl={false}
              interactive={false}
            >
              {station && (
                <Marker
                  longitude={station.location.longitude}
                  latitude={station.location.latitude}
                  anchor="bottom"
                  style={{ zIndex: 2 }}
                >
                  <div className={Styles.marker}>
                    <FontAwesomeIcon className={Styles.icon} icon={faMapPin} />
                  </div>
                </Marker>
              )}
            </Map>
          </div>
        </div>
      </div>
    </div>
  )
}
